import { retrieveFormById } from './api';
import { setFormDefaults } from './defaults';
import { enableAlwaysErrorMode } from './errors';
import { initializeExternalFormScripts } from './external-form-scripts';
import { initializeGeoAutocomplete } from './geo-autocomplete';
import { registerRecaptcha } from './recaptcha';
import { setSubmitListeners } from './submit';

export const renderForm = async (formDiv: Element, formId: number | string, formsUrl: string) => {
  // Set the HTML on the page
  // eslint-disable-next-line no-param-reassign
  formDiv.innerHTML = await retrieveFormById(formId, formsUrl);

  // Recreate the script so it can run in the browser
  const originalScripts = formDiv.querySelectorAll('script');
  originalScripts.forEach((originalScript) => {
    const script = document.createElement('script');
    script.innerHTML = originalScript.innerHTML;
    originalScript.parentNode.replaceChild(script, originalScript);
  });

  await initializeExternalFormScripts(formsUrl);
  await initializeGeoAutocomplete(formDiv);

  // Adjust the HTML
  const form = formDiv.querySelector(`form[data-formid="${formId}"]`);
  if (!form) {
    return;
  }

  // Set the listeners
  setSubmitListeners(form);
  registerRecaptcha(formDiv, formId);
  setFormDefaults(formDiv);

  enableAlwaysErrorMode(formDiv);
  form.setAttribute('data-action', form.getAttribute('action') ?? '');
  form.removeAttribute('action');

  const submitButton = formDiv.querySelector('input[type="submit"]');
  if (!submitButton) {
    return;
  }
  submitButton.removeAttribute('onkeypress');
  submitButton.removeAttribute('onclick');

  // Force the form to display, even if Gravity Forms is hiding it.
  const gformWrapper = formDiv.querySelector('.gform_wrapper');
  if (gformWrapper) {
    (gformWrapper as HTMLElement).style.display = '';
  }

  // Force a re-render of conditional logic.
  // A setTimeout is used to ensure the form is fully rendered before the event is triggered.
  setTimeout(() => {
    triggerGravityFormsRenderedEvent(formId);
    triggerRenderedEvent(formId, formDiv, form);
  }, 0);
};

/**
 * Informs Gravity Forms that the form has been rendered. This will also re-trigger any of the conditional field logic.
 */
const triggerGravityFormsRenderedEvent = (formId: string | number) => {
  // Re-process the conditional logic and any other mandatory scripts from GF.
  jQuery(document).trigger('gform_post_render', [formId, 1]);

  // Triggers the events that GF checks for determining the form has fully loaded.
  // We do this ourselves since all the scripts and forms are loaded in AJAX and load at different times.
  const gfThemeScriptsEvent = new Event('gform/theme/scripts_loaded');
  document.dispatchEvent(gfThemeScriptsEvent);

  // This also prevents any potential race conditions with the DOM being ready too soon.
  const domContentLoadedEvent = new Event('DOMContentLoaded', {
    bubbles: true,
    cancelable: true,
  });
  document.dispatchEvent(domContentLoadedEvent);
};

/**
 * Dispatches a custom event to inform the page that the form has been rendered.
 * This is useful for any custom scripts that need to run after the form has been rendered.
 */
const triggerRenderedEvent = (formId: string | number, formDiv: Element, form: Element) => {
  const customEvent = new CustomEvent('leadFormRendered', {
    detail: {
      formId,
      formDiv,
      form,
    },
  });

  // Dispatch the custom event
  document.dispatchEvent(customEvent);
};

export const disableSubmitButton = (formDiv: Element) => {
  const form = formDiv.querySelector('form');
  const submitButton = form.querySelector('input[type="submit"]');
  submitButton.disabled = true;
};

export const enableSubmitButton = (formDiv: Element) => {
  const form = formDiv.querySelector('form');
  const submitButton = form.querySelector('input[type="submit"]');
  submitButton.disabled = false;
};
